import React from 'react'
import { Box, Chip, Typography } from '@mui/material'
import { dsColorNew } from '@palette'
import { WarningAmber as WarningAmberIcon, Schedule as ScheduleIcon } from 'Pages/DsRepricing/@components/icons'
import { daysBetween, ruleHoursForDay } from '../mockData'
import { figuresOf, headlineOf, fmt } from '../@services/runSummary'
import { toneChipSx } from 'Pages/DsRepricing/@components/tones'

/**
 * A single day cell inside the month grid. Renders two stacks (rules /
 * strategies) with a clear warning when either is missing.
 */
export default function Cell({ cell, today, evaluation, selected, onClick, ran, ranUnavailable, onOpenChanges }) {
	if (!cell) {
		return <Box sx={{ minHeight: 130, bgcolor: dsColorNew.tableRowAlt,
		                  borderRight: 1, borderBottom: 1, borderColor: dsColorNew.grayBorder }}/>
	}
	const isToday = cell.iso === todayIso(today)
	const dba = daysBetween(today, cell.date)

	const hasRules = evaluation.applicableRules.length > 0
	const hasStrats = evaluation.applicableStrategies.length > 0
	// A day that has gone by: what ran, not what would apply.
	const past = dba < 0

	return (
		<Box onClick={onClick}
		     sx={{
			minHeight: 180, cursor: 'pointer', position: 'relative',
			borderRight: 1, borderBottom: 1, borderColor: dsColorNew.grayBorder,
			bgcolor: selected ? dsColorNew.pillActiveBg : dsColorNew.white,
			outline: selected ? '2px solid' : 'none',
			outlineColor: dsColorNew.appBlue,
			outlineOffset: -2,
			display: 'flex', flexDirection: 'column', gap: 0.75,
			p: 1.25,
			'&:hover': { bgcolor: selected ? dsColorNew.pillActiveBg : dsColorNew.hoverBgLight },
		}}>
			{/* Day number + DBA */}
			<Box display="flex" alignItems="baseline" gap={1}>
				<Typography sx={{ fontWeight: isToday ? 800 : 700,
				                  fontSize: 16, lineHeight: 1,
				                  color: isToday ? dsColorNew.appBlue : dsColorNew.textTitle }}>
					{cell.day}
				</Typography>
				{dba >= 0 && (
					<Typography sx={{ fontSize: 13, color: dsColorNew.textMuted,
					                  fontWeight: 500 }}>
						{dba === 0 ? 'today' : `${dba} DBA`}
					</Typography>
				)}
			</Box>

			{past ? (
				<RanBlock ran={ran} unavailable={ranUnavailable} onOpenChanges={onOpenChanges}/>
			) : (<>
			{/* Rules stack */}
			{hasRules ? (
				<Box display="flex" flexDirection="column" gap={0.5}>
					{evaluation.applicableRules.slice(0, 2).map(r => {
						const hours = ruleHoursForDay(r)
						return (
							<Box key={r.id} display="flex" alignItems="center" gap={0.6}
							     sx={{ fontSize: 14, color: dsColorNew.appBlue,
							           lineHeight: 1.2 }}>
								<Box component="span" sx={{
									display:'inline-flex', width: 22, height: 22,
									bgcolor: dsColorNew.pillActiveBg, color: dsColorNew.appBlue,
									borderRadius: '4px', alignItems:'center',
									justifyContent:'center', fontSize: 12,
									fontWeight: 700, flexShrink: 0 }}>
									{r.priority ?? '—'}
								</Box>
								<Box component="span" sx={{ fontWeight: 600, minWidth: 0,
								                            overflow: 'hidden',
								                            textOverflow: 'ellipsis',
								                            whiteSpace: 'nowrap' }}
								     title={r.name}>
									{r.name}
								</Box>
								<Box component="span" sx={{ color: dsColorNew.textMuted,
								                            fontWeight: 700, ml: 'auto',
								                            fontSize: 13 }}>
									{hours.length}×
								</Box>
							</Box>
						)
					})}
					{evaluation.applicableRules.length > 2 && (
						<Box sx={{ fontSize: 13, color: dsColorNew.textMuted,
						           fontWeight: 500 }}>
							+{evaluation.applicableRules.length - 2} more
						</Box>
					)}
				</Box>
			) : (
				<Box display="flex" alignItems="center" gap={0.6}
				     sx={{ fontSize: 13, color: dsColorNew.noteAmber,
				           fontWeight: 600 }}>
					<WarningAmberIcon sx={{ fontSize: 20 }}/>
					no schedule rule
				</Box>
			)}

			{/* divider */}
			<Box sx={{ borderTop: '1px dashed', borderColor: dsColorNew.grayBorder, my: 0.25 }}/>

			{/* Strategies stack */}
			{hasStrats ? (
				<Box display="flex" flexDirection="column" gap={0.5}>
					{evaluation.applicableStrategies.slice(0, 2).map(s => (
						<Box key={s.id} display="flex" alignItems="center" gap={0.6}
						     sx={{ fontSize: 14, color: dsColorNew.successText,
						           lineHeight: 1.2 }}>
							<Box component="span" sx={{
								display:'inline-flex', width: 22, height: 22,
								bgcolor: dsColorNew.successBg, color: dsColorNew.successText,
								borderRadius: '4px', alignItems:'center',
								justifyContent:'center', fontSize: 12,
								fontWeight: 700, flexShrink: 0 }}>
								{s.priority}
							</Box>
							<Box component="span" sx={{ fontWeight: 600, minWidth: 0,
							                            overflow: 'hidden',
							                            textOverflow: 'ellipsis',
							                            whiteSpace: 'nowrap' }}
							     title={s.name}>
								{s.name}
							</Box>
						</Box>
					))}
					{evaluation.applicableStrategies.length > 2 && (
						<Box sx={{ fontSize: 13, color: dsColorNew.textMuted,
						           fontWeight: 500 }}>
							+{evaluation.applicableStrategies.length - 2} more
						</Box>
					)}
				</Box>
			) : (
				<Box display="flex" alignItems="center" gap={0.6}
				     sx={{ fontSize: 13, color: dsColorNew.noteAmber,
				           fontWeight: 600 }}>
					<WarningAmberIcon sx={{ fontSize: 20 }}/>
					no active strategy
				</Box>
			)}
			</>)}
		</Box>
	)
}

/** The summary of a past day: passes and searches on one line, then
 *  the figures that are not zero — price changes, capped, errors, not
 *  sent… — as small chips. Four at most; the popover has them all. */
function RanBlock({ ran, unavailable, onOpenChanges }) {
	const headline = headlineOf(ran)
	if (!headline) {
		return (
			<Typography sx={{ fontSize: 13, color: dsColorNew.textSubtle, fontStyle: 'italic' }}>
				{unavailable ? '' : 'nothing ran'}
			</Typography>
		)
	}
	const figures = figuresOf(ran)
	return (
		<Box display="flex" flexDirection="column" gap={0.6}>
			<Typography sx={{ fontSize: 13, fontWeight: 700, color: dsColorNew.textTitle,
			                  lineHeight: 1.2 }}>
				{headline}
			</Typography>
			<Box display="flex" flexWrap="wrap" gap={0.5}>
				{figures.slice(0, 4).map(f => (
					<Chip key={f.key} size="small" label={`${fmt(f.n)} ${f.label}`}
					      title={f.query ? 'See these decisions' : undefined}
					      onClick={f.query && onOpenChanges
					          ? (e) => { e.stopPropagation(); onOpenChanges(f) }
					          : undefined}
					      sx={toneChipSx(f.tone, { clickable: !!f.query })}/>
				))}
				{figures.length > 4 && (
					<Box sx={{ fontSize: 12, color: dsColorNew.textMuted, fontWeight: 500,
					           alignSelf: 'center' }}>
						+{figures.length - 4} more
					</Box>
				)}
			</Box>
		</Box>
	)
}

function todayIso(today) {
	return `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`
}
